import {useState} from "react";
import {useParams, useHistory} from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, Chip, CircularProgress, TextField} from "@mui/material";
import BigSelector, {BigSelectorItem} from "../../components/common/BigSelector/BigSelector";
import {useAdminApi, ADMIN_ACCOUNTS_KEY, ADMIN_USERS_KEY} from "./api";

export function AccountDetails() {
    var {key} = useParams<{key: string}>();
    var history = useHistory();
    var api = useAdminApi();
    var queryClient = useQueryClient();

    var {data: accounts, isLoading: loadingAccounts} = useQuery(
        [ADMIN_ACCOUNTS_KEY],
        () => api.listAccounts(),
        {staleTime: 30000}
    );

    var {data: users, isLoading: loadingUsers} = useQuery(
        [ADMIN_USERS_KEY],
        () => api.listUsers(),
        {staleTime: 30000}
    );

    var account = accounts?.find(a => a.key === key);

    var [name, setName] = useState<string | null>(null);
    var [saving, setSaving] = useState(false);
    var [error, setError] = useState<string | null>(null);

    if (loadingAccounts) {
        return <div style={{display: "flex", justifyContent: "center", padding: 32}}><CircularProgress/></div>;
    }

    if (!account) {
        return <Alert severity="error" sx={{m: 2}}>Account "{key}" not found</Alert>;
    }

    var editName = name ?? account.name;

    async function handleSave() {
        try {
            setSaving(true);
            setError(null);
            await api.updateAccount(key, editName);
            await queryClient.invalidateQueries([ADMIN_ACCOUNTS_KEY]);
            history.push("/admin/accounts");
        } catch (e: any) {
            setError(e.message || "Failed to update account");
        } finally {
            setSaving(false);
        }
    }

    var relatedUsers = (users || []).filter(u => u.accountKey === key);

    var userItems: BigSelectorItem[] = relatedUsers.map(user => ({
        id: user.id.toString(),
        name: user.email,
        subtitle: [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined,
        icon: <i className="fas fa-user"/>,
        onActivate: () => history.push(`/admin/users/${user.id}`),
    }));

    return (
        <div>
            <Button startIcon={<i className="fas fa-arrow-left"/>} onClick={() => history.push("/admin/accounts")} sx={{mb: 2}}>
                Back
            </Button>

            <h2>Account: {key}</h2>

            <div style={{marginBottom: 16}}>
                <Chip
                    label={`Namespace: ${account.namespace}`}
                    icon={<i className="fas fa-layer-group" style={{fontSize: 14}}/>}
                    clickable
                    onClick={() => history.push(`/admin/namespaces/${account.namespace}`)}
                    sx={{mr: 1}}
                />
            </div>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            <div style={{maxWidth: 400, marginBottom: 24}}>
                <TextField
                    margin="dense"
                    label="Name"
                    fullWidth
                    variant="outlined"
                    value={editName}
                    onChange={(e) => setName(e.target.value)}
                    sx={{mb: 2}}
                />
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </div>

            <BigSelector
                title="Users"
                items={userItems}
                loading={loadingUsers}
                emptyMessage="No users in this account"
            />
        </div>
    );
}
